import { useState, useEffect, useRef, useCallback } from 'react';

interface DocumentThumbnailProps {
  document: {
    id: string;
    fileType: string;
    fileName: string;
  };
  getDownloadUrl: (variables: { variables: { id: string } }) => Promise<any>;
  size?: number;
  onClick?: () => void;
}

// Helper to check if file is an image
const isImageFile = (fileType: string): boolean => {
  return fileType?.startsWith('image/') || false;
};

// Helper function for file icons
export const getFileIcon = (fileType: string): string => {
  if (!fileType) return '📎';
  if (fileType.startsWith('image/')) return '🖼️';
  if (fileType.includes('pdf')) return '📄';
  if (fileType.includes('word') || fileType.includes('document')) return '📝';
  if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📊';
  return '📎';
};

export function DocumentThumbnail({ 
  document, 
  getDownloadUrl,
  size = 56,
  onClick,
}: DocumentThumbnailProps) {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [imageError, setImageError] = useState(false);
  const loadedIdRef = useRef<string | null>(null);
  const mountedRef = useRef(true);

  // Memoize the load function to prevent unnecessary re-renders
  const loadThumbnail = useCallback(async () => {
    // Skip if not an image or already loaded this document
    if (!isImageFile(document.fileType)) {
      setLoading(false);
      return;
    }
    
    // Skip if we already loaded this exact document
    if (loadedIdRef.current === document.id && thumbnailUrl) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(false);
    setImageError(false);
    
    try {
      const result = await getDownloadUrl({ variables: { id: document.id } });
      
      if (!mountedRef.current) return;
      
      // Handle different response structures
      const url = result?.data?.getDocumentDownloadUrl || 
                  result?.data?.getInquiryAttachmentDownloadUrl ||
                  result?.getDocumentDownloadUrl;
      
      if (url) {
        loadedIdRef.current = document.id;
        setThumbnailUrl(url);
      } else {
        setError(true);
      }
    } catch (err) {
      console.error('Error loading thumbnail:', err);
      if (mountedRef.current) {
        setError(true);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [document.id, document.fileType, getDownloadUrl, thumbnailUrl]);

  useEffect(() => {
    mountedRef.current = true;
    
    // Only load if document ID changed
    if (loadedIdRef.current !== document.id) {
      loadThumbnail();
    } else {
      setLoading(false);
    }
    
    return () => {
      mountedRef.current = false;
    };
  }, [document.id, loadThumbnail]);

  const containerStyle: React.CSSProperties = {
    width: size,
    height: size,
    borderRadius: 8,
    backgroundColor: '#F8F9FA',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
    cursor: onClick ? 'pointer' : 'default',
  };

  const imageStyle: React.CSSProperties = {
    width: size,
    height: size,
    borderRadius: 8,
    objectFit: 'cover',
  };

  const handleClick = () => {
    if (onClick) onClick();
  };

  const handleImageError = () => {
    setImageError(true);
  };

  // Not an image - show icon
  if (!isImageFile(document.fileType)) {
    return (
      <div 
        style={containerStyle} 
        onClick={handleClick}
        className="hover:bg-gray-200 transition-colors"
        title={`Preview ${document.fileName}`}
      >
        <span style={{ fontSize: size * 0.43 }}>{getFileIcon(document.fileType)}</span>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div style={containerStyle}>
        <div className="animate-pulse bg-gray-300 w-full h-full rounded-lg" />
      </div>
    );
  }

  // Error or no URL or image failed to load - show image icon
  if (error || !thumbnailUrl || imageError) {
    return (
      <div 
        style={containerStyle} 
        onClick={handleClick}
        className="hover:bg-gray-200 transition-colors"
        title={`Preview ${document.fileName}`}
      >
        <span style={{ fontSize: size * 0.43 }}>🖼️</span>
      </div>
    );
  }

  // Show thumbnail
  return (
    <img 
      src={thumbnailUrl} 
      alt={document.fileName}
      style={imageStyle}
      onClick={handleClick}
      onError={handleImageError}
      className="hover:opacity-80 transition-opacity cursor-pointer"
      title={`Preview ${document.fileName}`}
      loading="lazy"
    />
  );
}
